import { useState } from 'react'
import type { MissionDto } from '@/api/hooks'
import {
  useLaunchBlackMarket,
  useLaunchBuy,
  useLaunchRaid,
  useLaunchSalvage,
  useLaunchSmuggle,
  useLaunchTradeRun,
  useMyFleet,
} from '@/api/hooks'

/**
 * Mission types whose payload carries the FULL launch config (fleet + destination
 * + cargo/buy) → one-click repeat. Exploration/scouting/patrol are excluded: an
 * explored island stays explored, the patrol is onboarding-only, and a scout run
 * belongs to the PvP panel.
 */
const RELAUNCHABLE_TYPES = new Set(['commerce', 'smuggling', 'raid_pnj', 'recif_salvage'])

/**
 * « Relancer la même mission » — re-sends the exact configuration of a completed
 * mission (same fleet, same destination, same cargo) through the regular launch
 * endpoints, which re-validate and re-price everything server-side. Only offered
 * while the WHOLE original fleet is docked: a partial convoy would silently launch
 * a different mission (smaller hold, weaker raid) than the one being repeated.
 */
export function RelaunchMissionButton({ mission }: { mission: MissionDto }) {
  const fleet = useMyFleet()
  const tradeSell = useLaunchTradeRun()
  const tradeBuy = useLaunchBuy()
  const smuggleSell = useLaunchSmuggle()
  const blackMarket = useLaunchBlackMarket()
  const raid = useLaunchRaid()
  const salvage = useLaunchSalvage()
  const [error, setError] = useState<string | null>(null)

  if (mission.status !== 'completed' || !RELAUNCHABLE_TYPES.has(mission.type)) return null
  const destinationIslandId = mission.destinationIslandId
  if (destinationIslandId == null) return null
  const shipIds = mission.payload.fleetShipIds ?? (mission.shipId != null ? [mission.shipId] : [])
  if (shipIds.length === 0) return null

  const ships = fleet.data?.ships
  const missing = ships == null ? null : shipIds.filter((id) => !ships.some((s) => s.id === id && s.status === 'docked'))
  const ready = missing != null && missing.length === 0

  const pending =
    tradeSell.isPending || tradeBuy.isPending || smuggleSell.isPending
    || blackMarket.isPending || raid.isPending || salvage.isPending

  const relaunch = () => {
    setError(null)
    const p = mission.payload
    const opts = { onError: (err: Error) => setError(err.message) }
    // payload.buy.resource is a ResourceType value ('rum'); the buy endpoints
    // speak IslandSpecialty ('rhum') — map it (mirror of the launch panels).
    const boughtResource = p.buy?.resource === 'rum' ? ('rhum' as const) : ('wood' as const)
    if (mission.type === 'raid_pnj') {
      raid.mutate({ shipIds, islandId: destinationIslandId }, opts)
    } else if (mission.type === 'recif_salvage') {
      salvage.mutate({ shipIds, destinationIslandId }, opts)
    } else if (mission.type === 'commerce' && p.buy) {
      tradeBuy.mutate({ shipIds, destinationIslandId, resource: boughtResource, wood: p.buy.amount ?? p.buy.wood ?? 0 }, opts)
    } else if (mission.type === 'commerce') {
      tradeSell.mutate({ shipIds, destinationIslandId, wood: p.cargo?.wood ?? 0, rum: p.cargo?.rum ?? 0 }, opts)
    } else if (p.buy) {
      blackMarket.mutate({ shipIds, destinationIslandId, resource: boughtResource, amount: p.buy.amount ?? 0 }, opts)
    } else {
      smuggleSell.mutate({ shipIds, destinationIslandId, wood: p.cargo?.wood ?? 0, rum: p.cargo?.rum ?? 0 }, opts)
    }
  }

  // Icône discrète (sous le label de statut de la carte), pas un gros CTA : la
  // relance est un raccourci, pas l'action principale du rapport.
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={relaunch}
        disabled={!ready || pending}
        aria-label="Relancer la même mission"
        title={
          ready
            ? 'Relancer la même mission (même flotte, même destination, même cargaison)'
            : missing === null
              ? 'Flotte en cours de chargement…'
              : "La flotte d'origine n'est pas au complet à quai"
        }
        className="w-7 h-7 rounded-full border border-sand-500/25 bg-ocean-900/60 text-sand-300 hover:bg-ocean-700 hover:text-sand-100 transition-colors disabled:opacity-35 disabled:cursor-not-allowed flex items-center justify-center"
      >
        <RestartIcon className={`w-3.5 h-3.5 ${pending ? 'animate-spin' : ''}`} />
      </button>
      {error && <p className="text-[10px] text-rum-300 text-right max-w-[200px]">{error}</p>}
    </div>
  )
}

function RestartIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  )
}
